import { useState, useContext, useEffect, useMemo } from "react";
import { ApiContext } from "../../../../context/MyContext";
import { useUtils } from "../../../../context/UtilsContext";
import Button from "../../../../common/Button/Button";
import {
  verifyEmployee,
  fetchCommonUtils,
  modifyEmployeeRoles,
  fetchEmployees,
} from "../../../../service/employeeService";
import { resolveModifyRoleEventTypeId } from "../resolveModifyRoleEventTypeId";
import {
  getApiErrorMessage,
  getSessionEventTypes,
} from "../employeeListUtils";

const todayIsoDate = () => new Date().toISOString().split("T")[0];

const VerifyEmpModal = ({
  onClose,
  employee,
  onVerifySuccess,
  departments,
  onToast,
}) => {
  const { post, get } = useContext(ApiContext);
  const { sessionUtils } = useUtils();

  const rawEmployeeId = employee.employee_id || employee.id || null;
  const employeeId =
    rawEmployeeId && rawEmployeeId !== employee.user_id ? rawEmployeeId : null;

  const [selectedDepartment, setSelectedDepartment] = useState("");
  const [selectedRole, setSelectedRole] = useState("");
  const [selectedManager, setSelectedManager] = useState("");
  const [hireDate, setHireDate] = useState(todayIsoDate);
  const [loading, setLoading] = useState(false);
  const [managers, setManagers] = useState([]);

  const roleEventTypeId = useMemo(
    () => resolveModifyRoleEventTypeId(getSessionEventTypes(sessionUtils)),
    [sessionUtils]
  );

  const roles = useMemo(() => {
    if (!selectedDepartment) return [];
    return (
      departments.find((dept) => String(dept.id) === String(selectedDepartment))
        ?.roles || []
    );
  }, [departments, selectedDepartment]);

  useEffect(() => {
    const loadManagers = async () => {
      try {
        const response = await fetchCommonUtils(get);
        let users = response?.users || [];

        if (!users.length) {
          const { employees: empList } = await fetchEmployees(get);
          users = (empList || []).map((emp) => ({
            user_id: emp.user_id,
            first_name: emp.first_name || emp.name?.split(" ")[0] || "",
            last_name:
              emp.last_name ||
              (emp.name ? emp.name.split(" ").slice(1).join(" ") : ""),
          }));
        }

        setManagers(users);
      } catch (error) {
        console.error("Error fetching managers:", error);
      }
    };
    loadManagers();
  }, [get]);

  const handleVerifyEmployee = async () => {
    if (!selectedDepartment || !selectedRole) {
      onToast("Please select both department and role", "error");
      return;
    }
    if (!selectedManager) {
      onToast("Please select a manager", "error");
      return;
    }
    if (!roleEventTypeId) {
      onToast("Event type for modifying roles is missing.", "error");
      return;
    }

    try {
      setLoading(true);
      await verifyEmployee(post, {
        employee_id: employeeId,
        user_id: employee.user_id,
        department_id: selectedDepartment,
        role_id: selectedRole,
        manager_id: selectedManager,
        hire_date: hireDate,
      });

      let roleEmployeeId = employeeId;
      try {
        const { employees: empList } = await fetchEmployees(get);
        const matched = (empList || []).find(
          (emp) => emp.user_id === employee.user_id
        );
        roleEmployeeId = matched?.employee_id || matched?.id || employeeId;
      } catch (err) {
        console.error("Failed to refetch employees for id:", err);
      }

      if (!roleEmployeeId) {
        onToast(
          "Employee id is missing after verification. Please refresh and try again.",
          "error"
        );
        return;
      }

      await modifyEmployeeRoles(post, {
        event_type_id: roleEventTypeId,
        role_assignments: [
          {
            operation: "ADD",
            employee_id: roleEmployeeId,
            role_id: selectedRole,
            active_from: hireDate
              ? new Date(hireDate).toISOString()
              : new Date().toISOString(),
            is_primary: true,
          },
        ],
      });

      onToast("Employee Verified!", "success");
      onVerifySuccess();
    } catch (error) {
      console.error("Error verifying employee:", error.message);
      onToast(getApiErrorMessage(error, "Failed to verify employee"), "error");
    } finally {
      setLoading(false);
    }
  };

  const canSubmit =
    Boolean(selectedDepartment && selectedRole && selectedManager) && !loading;

  return (
    <div
      className="fixed inset-0 bg-orange-primary bg-opacity-20 flex justify-center items-center z-50"
      onClick={onClose}
    >
      <div
        className="bg-white p-6 rounded-lg shadow-lg w-[500px] min-w-[400px] max-w-[600px] max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="text-xl font-semibold mb-4">
          Verify Employee - {employee.name}
        </h2>

        <label className="block text-gray-700 font-semibold mb-2">
          Assign Department *
        </label>
        <select
          className="w-full p-2 border rounded-md mb-4"
          value={selectedDepartment}
          onChange={(e) => {
            setSelectedDepartment(e.target.value);
            setSelectedRole("");
          }}
        >
          <option value="">-- Select Department --</option>
          {departments.map((dept) => (
            <option key={dept.id} value={dept.id}>
              {dept.name}
            </option>
          ))}
        </select>

        {roles.length > 0 && (
          <>
            <label className="block text-gray-700 font-semibold mb-2">
              Assign Role *
            </label>
            <select
              className="w-full p-2 border rounded-md mb-4"
              value={selectedRole}
              onChange={(e) => setSelectedRole(e.target.value)}
            >
              <option value="">-- Select Role --</option>
              {roles.map((role) => (
                <option key={role.id} value={role.id}>
                  {role.title}
                </option>
              ))}
            </select>
          </>
        )}

        <label className="block text-gray-700 font-semibold mb-2">
          Assign Manager *
        </label>
        <select
          className="w-full p-2 border rounded-md mb-4"
          value={selectedManager}
          onChange={(e) => setSelectedManager(e.target.value)}
        >
          <option value="">-- Select Manager --</option>
          {managers.map((manager) => (
            <option key={manager.user_id} value={manager.user_id}>
              {manager.first_name} {manager.last_name}
            </option>
          ))}
        </select>

        <label className="block text-gray-700 font-semibold mb-2">
          Hire Date *
        </label>
        <input
          type="date"
          className="w-full p-2 border rounded-md mb-4"
          value={hireDate}
          onChange={(e) => setHireDate(e.target.value)}
        />

        <div className="flex justify-end space-x-2">
          <Button
            text="Cancel"
            className="bg-gray-300 text-gray-800 px-4 py-2 rounded-md"
            onClick={onClose}
            disabled={loading}
          />
          <Button
            text={loading ? "Verifying..." : "Verify"}
            className={`px-4 py-2 rounded-md ${
              canSubmit
                ? "bg-orange-primary text-white"
                : "bg-gray-300 text-gray-800 cursor-not-allowed"
            }`}
            disabled={!canSubmit}
            onClick={handleVerifyEmployee}
          />
        </div>
      </div>
    </div>
  );
};

export default VerifyEmpModal;
